import { ToastType } from '@components';
import { GroupMapping } from '@store/billingQueue/constants';
import { globalState } from '@store/Global/store';
import {
  deserializeWsDataJson,
  deserializeWsMessage,
} from '@store/Global/socketMessage';
import { WSEventTypeEnum } from '@store/Global/types';
import { deepConvertPascalToCamelCase } from '@utils/caseTransformers';
import { handleToast } from '@utils/utils';
import size from 'lodash/size';

import { BillingQueueStore } from './consts';
import {
  addToBillingQueueApi,
  deleteLoadDocumentApi,
  fetchBillingApprovedQueueApi,
  fetchBillingDisputedQueueApi,
  fetchBillingPendingApprovalQueueApi,
  fetchBillingRespondedQueueApi,
  fetchBillingUploadQueueApi,
  fetchLoadDocumentApi,
  fetchLoadDocumentByBatchApi,
  fetchLoadDocumentByIdApi,
  markLoadQuickpayApi,
  moveBatchApi,
  reImportBatchApi,
} from './services';
import { setBillingQueueStore } from './store';
import { DocumentRecord } from './types';

export const getBillingQueueUploadData = async (payload: string) => {
  try {
    setBillingQueueStore('uploadQueue', 'loading', true);
    const data = await fetchBillingUploadQueueApi(payload);
    setBillingQueueStore('uploadQueue', 'data', data);
  } finally {
    setBillingQueueStore('uploadQueue', 'loading', false);
  }
};

export const getLoadDocumentData = async (
  payload: string,
  isRefreshBtn: boolean,
  isInitialLoad: boolean = false,
) => {
  try {
    setBillingQueueStore('loadDocument', 'loading', true);
    if (isRefreshBtn || isInitialLoad) {
      const data = await fetchLoadDocumentApi();
      if (data) {
        setBillingQueueStore('loadDocument', 'data', data);
        setBillingQueueStore(
          'noaDocument',
          'data',
          filterLoadNoaDocument(data),
        );
        setBillingQueueStore(
          'reviewDocument',
          'data',
          filterLoadReviewDocument(data, payload),
        );
        setBillingQueueStore(
          'unknownDocument',
          'data',
          filterLoadUnkownDocument(data),
        );
        setBillingQueueStore('loadDocument', 'filteredDataCount', size(data));
      }
    }
  } finally {
    setBillingQueueStore('loadDocument', 'loading', false);
  }
};

const filterLoadNoaDocument = (data: DocumentRecord[]) =>
  data.filter((doc) => {
    if (!doc.documents) {
      return false;
    }

    const fileNames = doc.documents.split(';');

    const filesTypes: Record<string, number> = {
      EML: fileNames.filter((x) => x.includes('EML')).length,
      NOA: fileNames.filter((x) => x.includes('NOA')).length,
      BOL: fileNames.filter((x) => x.includes('BOL')).length,
      RCF: fileNames.filter((x) => x.includes('RCF')).length,
      CIN: fileNames.filter((x) => x.includes('CIN')).length,
      MISC: fileNames.filter((x) => x.includes('MISC')).length,
    };
    return (
      filesTypes['EML'] > 0 &&
      filesTypes['NOA'] > 0 &&
      filesTypes['BOL'] === 0 &&
      filesTypes['RCF'] === 0 &&
      filesTypes['CIN'] === 0 &&
      filesTypes['MISC'] === 0
    );
  });

export const filterLoadReviewDocument = (
  data: DocumentRecord[],
  group: string,
) => {
  let docs = data.filter((doc) => doc.loadId !== null);
  if (group !== '' && group !== 'All Groups') {
    docs = docs.filter(
      (doc) =>
        Number(doc.accountingGroup) ===
        parseInt(GroupMapping[group as keyof typeof GroupMapping]),
    );
  }

  return docs.filter((doc) => doc.documents !== '1.tif=EML;2.tif=NOA;');
};

const filterLoadUnkownDocument = (data: DocumentRecord[]) =>
  data.filter((doc) => {
    if (doc.loadId !== null) return false;
    const item = doc.documents;
    if (!item) return false;

    const fileNames = item.split(';');
    const filesTypes: Record<string, number> = {
      EML: fileNames.filter((x) => x.includes('EML')).length,
      NOA: fileNames.filter((x) => x.includes('NOA')).length,
      BOL: fileNames.filter((x) => x.includes('BOL')).length,
      RCF: fileNames.filter((x) => x.includes('RCF')).length,
      CIN: fileNames.filter((x) => x.includes('CIN')).length,
      MISC: fileNames.filter((x) => x.includes('MISC')).length,
    };

    if (
      filesTypes['EML'] > 0 &&
      filesTypes['NOA'] > 0 &&
      filesTypes['BOL'] === 0 &&
      filesTypes['RCF'] === 0 &&
      filesTypes['CIN'] === 0 &&
      filesTypes['MISC'] === 0
    ) {
      return false;
    }
    return true;
  });

export const getLoadDocumentById = async (id: string) => {
  try {
    setBillingQueueStore('loadDocument', 'loading', true);

    const data = await fetchLoadDocumentByIdApi(id);
    if (data) {
      setBillingQueueStore('loadDocumentMetaData', data);
    }
  } finally {
    setBillingQueueStore('loadDocument', 'loading', false);
  }
};

export const reImportBacth = async (batchId: string) => {
  try {
    setBillingQueueStore('loadDocument', 'reImportLoading', true);
    const data = await reImportBatchApi(batchId);
    if (data && data.success) {
      handleToast(ToastType.Success, 'Batch queued to re-import');
      const socketv = globalState.socketv;
      if (socketv !== undefined) {
        const handler = (message: unknown) => {
          const msgObj = deserializeWsMessage(message);
          if (msgObj == null) {
            return;
          }

          const data = deserializeWsDataJson<Record<string, unknown>>(
            msgObj.dataJson,
          );
          if (data == null) {
            return;
          }

          const metaModel: DocumentRecord = deepConvertPascalToCamelCase(data);
          if (
            msgObj.eventName === WSEventTypeEnum.BillingQueueTransfloAdded ||
            msgObj.eventName === WSEventTypeEnum.BillingQueueTransfloImported
          ) {
            if (metaModel.batchId === batchId) {
              handleToast(
                ToastType.Success,
                `Documents for BatchId:  ${metaModel.batchId} was re-imported successfully`,
              );
              socketv.off('RecieveWebSocketMessage', handler);
            }
          }
        };
        await socketv.invoke(
          'Subscribe',
          WSEventTypeEnum.BillingQueueTransfloAdded,
        );
        await socketv.invoke(
          'Subscribe',
          WSEventTypeEnum.BillingQueueTransfloImported,
        );
        socketv.on('RecieveWebSocketMessage', handler);
      }
    }
  } finally {
    setBillingQueueStore('loadDocument', 'reImportLoading', false);
  }
};

export const deleteLoadDocument = async (
  id: number,
  type: 'noticeOfAssignment' | 'unknownDocument' | 'reviewDocument',
) => {
  try {
    setBillingQueueStore('loadDocument', 'loading', true);
    const data = await deleteLoadDocumentApi(id);
    if (data && data.success) {
      const docTypeToStoreKey: Record<
        'noticeOfAssignment' | 'unknownDocument' | 'reviewDocument',
        'noaDocument' | 'unknownDocument' | 'reviewDocument'
      > = {
        noticeOfAssignment: 'noaDocument',
        unknownDocument: 'unknownDocument',
        reviewDocument: 'reviewDocument',
      };

      const storeKey = docTypeToStoreKey[type];
      if (Boolean(storeKey)) {
        setBillingQueueStore(storeKey, 'data', (prevData) =>
          prevData.filter((doc) => doc.id !== id),
        );
      }
      handleToast(ToastType.Success, 'Document deleted');
    }
  } finally {
    setBillingQueueStore('loadDocument', 'loading', false);
  }
};

export const getBillingQueueApprovedData = async (payload: string) => {
  try {
    setBillingQueueStore('approvedQueue', 'loading', true);
    const data = await fetchBillingApprovedQueueApi(payload);
    if (data) {
      setBillingQueueStore('approvedQueue', 'data', data);
    }
  } finally {
    setBillingQueueStore('approvedQueue', 'loading', false);
  }
};

export const getBillingQueuePendingApprovalData = async (payload?: string) => {
  try {
    setBillingQueueStore('pendingApprovalQueue', 'loading', true);
    const data = await fetchBillingPendingApprovalQueueApi(payload);
    if (data) {
      setBillingQueueStore('pendingApprovalQueue', 'data', data);
    }
  } finally {
    setBillingQueueStore('pendingApprovalQueue', 'loading', false);
  }
};

export const getBillingQueueRespondedData = async (payload: string) => {
  try {
    setBillingQueueStore('respondedQueue', 'loading', true);
    const data = await fetchBillingRespondedQueueApi(payload);
    if (data) {
      setBillingQueueStore('respondedQueue', 'data', data);
    }
  } finally {
    setBillingQueueStore('respondedQueue', 'loading', false);
  }
};

export const moveBatch = async (payload: {
  batchId: string;
  loadNumber: string;
}) => {
  try {
    setBillingQueueStore('loadDocument', 'loading', true);
    const data = await moveBatchApi(payload);
    if (data && data.success) {
      handleToast(
        ToastType.Success,
        `Success! Files moved to LoadNumber ${payload.loadNumber}`,
      );
      return data;
    }
  } finally {
    setBillingQueueStore('loadDocument', 'loading', false);
  }
};

export const markLoadQuickpay = async (payload: {
  quickPay: boolean;
  loadNumber: string;
}) => {
  try {
    setBillingQueueStore('loadDocument', 'loading', true);
    const response = await markLoadQuickpayApi(payload);
    if (response && response.success) {
      handleToast(ToastType.Success, response.data);
    }
  } finally {
    setBillingQueueStore('loadDocument', 'loading', false);
  }
};

export const addToBillingQueue = async (payload: {
  quickPay: boolean;
  loadNumber: string;
}) => {
  try {
    setBillingQueueStore('loadDocument', 'loading', true);
    const response = await addToBillingQueueApi(payload);
    if (response && response.success) {
      handleToast(
        ToastType.Success,
        'Documents will be processed and added to the queue',
      );
    }
    return response;
  } finally {
    setBillingQueueStore('loadDocument', 'loading', false);
  }
};
export const getBillingQueueDisputedData = async (payload: string) => {
  try {
    setBillingQueueStore('disputedQueue', 'loading', true);
    const data = await fetchBillingDisputedQueueApi(payload);
    if (data) {
      setBillingQueueStore('disputedQueue', 'data', data);
    }
  } finally {
    setBillingQueueStore('disputedQueue', 'loading', false);
  }
};

export const getLoadDocumentByBatch = async (id: string) => {
  try {
    setBillingQueueStore('loadDocument', 'loading', true);
    // reset the loadDocumentMetaData
    setBillingQueueStore(
      'loadDocumentMetaData',
      BillingQueueStore.loadDocumentMetaData,
    );
    const data = await fetchLoadDocumentByBatchApi(id);
    if (data) {
      setBillingQueueStore('loadDocumentMetaData', 'uploaded', data);
    }
  } finally {
    setBillingQueueStore('loadDocument', 'loading', false);
  }
};
